import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { CONTACTS } from "./data";

const FIELDS = [
  { key: "azienda", label: "Azienda", placeholder: "Solo se richiedi come azienda", testid: "b2b-input-azienda", required: false },
  { key: "referente", label: "Nome e Cognome", placeholder: "Es. Mario Rossi", testid: "b2b-input-referente", required: true },
  { key: "piva", label: "Partita IVA", placeholder: "11 cifre — serve solo alle aziende", testid: "b2b-input-piva", required: false },
  { key: "telefono", label: "Telefono", placeholder: "Es. 333 1234567", testid: "b2b-input-telefono", required: true },
  { key: "email", label: "Email", placeholder: "Es. mario@rossi.it", testid: "b2b-input-email", required: true },
];

const EMPTY = { azienda: "", referente: "", piva: "", telefono: "", email: "", note: "" };

export const B2BQuoteModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-b2b-modal", handler);
    return () => window.removeEventListener("open-b2b-modal", handler);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.referente.trim()) er.referente = "Inserisci nome e cognome";
    if (form.piva.trim() && !/^\d{11}$/.test(form.piva.trim())) er.piva = "Se indicata, la Partita IVA deve avere 11 cifre";
    if (!/^[\d\s+]{6,}$/.test(form.telefono.trim())) er.telefono = "Inserisci un telefono valido";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Inserisci un'email valida";
    return er;
  };

  const submit = () => {
    const er = validate();
    if (Object.keys(er).length > 0) {
      setErrors(er);
      toast.error("Compila tutti i campi obbligatori per procedere.");
      return;
    }
    const lines = ["Richiesta preventivo — Computer & periferiche"];
    if (form.azienda.trim()) lines.push(`Azienda: ${form.azienda.trim()}`);
    lines.push(`Nome: ${form.referente.trim()}`);
    if (form.piva.trim()) lines.push(`P.IVA: ${form.piva.trim()}`);
    lines.push(`Telefono: ${form.telefono.trim()}`, `Email: ${form.email.trim()}`);
    if (form.note.trim()) lines.push(`Note: ${form.note.trim()}`);
    const url = `https://wa.me/${CONTACTS.mobileRaw.replace("+", "")}?text=${encodeURIComponent(lines.join("\n"))}`;
    toast.success("Perfetto! Apro WhatsApp con la tua richiesta.");
    window.open(url, "_blank");
    setOpen(false);
    setForm(EMPTY);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="b2b-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[70] bg-[#181817]/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="w-full max-w-lg bg-[#FBF9F5] border border-[#E5E2DC] rounded-sm p-8 sm:p-10 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C85A32]">Preventivo PC</p>
                <h3 className="mt-2 font-display text-3xl font-light text-[#181817]">Preventivo Computer & Periferiche</h3>
              </div>
              <button data-testid="b2b-close-button" onClick={() => setOpen(false)} className="text-[#6E6D69] hover:text-[#181817] transition-colors" aria-label="Chiudi">
                <X size={20} />
              </button>
            </div>

            <div className="mt-5 flex items-start gap-3 bg-[#EBE6DD] rounded-sm p-4">
              <ShieldCheck size={18} className="text-[#C85A32] shrink-0 mt-0.5" />
              <p className="text-xs text-[#181817]/75 leading-relaxed">
                Compila i tuoi dati e invia: la richiesta arriva direttamente su WhatsApp. La Partita IVA è facoltativa — serve solo ad aziende e professionisti.
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">
                    {f.label}{f.required ? " *" : " · facoltativo"}
                  </label>
                  <input
                    data-testid={f.testid}
                    value={form[f.key]}
                    onChange={set(f.key)}
                    placeholder={f.placeholder}
                    className={`mt-1.5 w-full bg-white border rounded-sm px-4 py-2.5 text-sm outline-none transition-colors duration-300 ${errors[f.key] ? "border-[#C0392B]" : "border-[#E5E2DC] focus:border-[#181817]"}`}
                  />
                  {errors[f.key] && (
                    <p data-testid={`b2b-error-${f.key}`} className="mt-1 text-xs text-[#C0392B]">{errors[f.key]}</p>
                  )}
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Note · facoltativo</label>
                <textarea
                  data-testid="b2b-input-note"
                  value={form.note}
                  onChange={set("note")}
                  rows={3}
                  placeholder="Es. PC da riparare, oppure preventivo per una postazione completa..."
                  className="mt-1.5 w-full bg-white border border-[#E5E2DC] rounded-sm px-4 py-2.5 text-sm outline-none focus:border-[#181817] transition-colors duration-300 resize-none"
                />
              </div>
            </div>

            <button
              data-testid="b2b-submit-button"
              onClick={submit}
              className="mt-7 w-full bg-[#25D366] text-white py-3.5 rounded-full text-sm font-medium hover:opacity-90 transition-opacity duration-300"
            >
              Invia la richiesta su WhatsApp
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-[#6E6D69]">
              La richiesta arriva direttamente a noi su WhatsApp
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
